import { TZDate } from '@date-fns/tz'
import {
  addDays,
  addHours,
  addMinutes,
  isAfter,
  startOfDay,
  setMinutes,
  setHours,
  max,
} from 'date-fns'

import type { PostCard_PostData } from '~publish/components/PostCard/PostCard'
import type { ResultOf } from '~publish/graphql'
import type { CalendarPostCard_PostData } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'
import { getUTCString } from '~publish/helpers/dateFormatters'
import { memoize } from '~publish/helpers/memoize'
import { logError } from '~publish/legacy/utils/logError'

import type { QueueList_Channel } from './fragments/QueueList_Channel'

const MAX_DAYS_TO_GENERATE = 730 // 2 years

/** One day of a channel's posting schedule, not the whole week. */
export type ScheduleV2 = ResultOf<
  typeof QueueList_Channel
>['postingSchedule'][number]

export type Slot = { date: string }
export type SlotOrPost<T> = Slot | (T & { dueAt: string })

/**
 * Generate slots according to the posting schedule until a certain date
 * @param postingSchedule - The schedule for the channel
 * @param endDate - The date until which slots should be generated
 * @param timezone - The timezone of the channel
 * @returns
 */
export const generatePostingSlotsUntilDate = memoize(function ({
  postingSchedule,
  timezone,
  endDate: endDateISO,
}: {
  postingSchedule: ScheduleV2[]
  timezone: string
  endDate?: string
}): Slot[] {
  const slots: Slot[] = []
  if (!postingSchedule.length || !endDateISO) {
    return slots
  }

  const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

  const startDate = new TZDate(Date.now(), timezone)
  const endDate = new TZDate(endDateISO, timezone)

  // Guard against invalid endDate which would cause isAfter to always
  // return false, creating an infinite loop
  if (Number.isNaN(endDate.getTime())) {
    return slots
  }

  let currentDate = startOfDay(startDate)
  let daysIterated = 0

  while (
    !isAfter(currentDate, endDate) &&
    daysIterated < MAX_DAYS_TO_GENERATE
  ) {
    const daySchedule = postingSchedule.find(
      (schedule) => schedule.day === days[currentDate.getDay()],
    )

    if (daySchedule) {
      for (const time of daySchedule.times) {
        const [hours, minutes] = time.split(':').map(Number)
        if (Number.isNaN(hours) || Number.isNaN(minutes)) continue

        let slotDate = addHours(addMinutes(currentDate, minutes), hours)

        // If the slot is not on the set hour, we found a DST hour that is
        // skipped in the timezone, so we correct the time applying the delta
        // between the set hour and the actual hour.
        if (slotDate.getHours() !== hours) {
          const delta = hours - slotDate.getHours()
          slotDate = addHours(slotDate, delta)
        }

        // If the slot is the same as the previous slot, skip it
        // We do not allow duplicate slots in the backend and this could happen
        // when the slot is defined on a DST hour that is skipped in the timezone
        const prevSlot = slots.at(-1)
        if (prevSlot && prevSlot.date === slotDate.toISOString()) {
          continue
        }

        if (isAfter(slotDate, startDate) && !isAfter(slotDate, endDate)) {
          slots.push({
            date: getUTCString(slotDate),
          })
        }
      }
    }

    currentDate = startOfDay(addDays(currentDate, 1))
    daysIterated++
  }

  return slots
})

/**
 * Generate a certain number of slots according to the posting schedule and the provided numberOfSlots
 * @param postingSchedule - The schedule for the channel
 * @param numberOfSlots - The number of slots to generate
 * @param timezone - The timezone of the channel
 * @returns
 */
const generatePostingSlotsByNumber = memoize(function ({
  postingSchedule,
  timezone,
  numberOfSlots,
}: {
  postingSchedule: ScheduleV2[]
  timezone: string
  numberOfSlots: number
}): Slot[] {
  const slots: Slot[] = []
  const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
  const startDate = new TZDate(Date.now(), timezone)
  let currentDate = startOfDay(startDate)
  let daysIterated = 0

  while (slots.length < numberOfSlots && daysIterated < MAX_DAYS_TO_GENERATE) {
    const dayOfWeek = days[currentDate.getDay()]
    const daySchedule = postingSchedule.find(
      (schedule) => schedule.day === dayOfWeek,
    )

    if (daySchedule && !daySchedule.paused) {
      for (const time of daySchedule.times) {
        if (slots.length >= numberOfSlots) break

        const [hours, minutes] = time.split(':').map(Number)
        if (Number.isNaN(hours) || Number.isNaN(minutes)) continue

        const slotDate = setHours(setMinutes(currentDate, minutes), hours)

        if (isAfter(slotDate, startDate)) {
          slots.push({
            date: getUTCString(slotDate),
          })
        }
      }
    }

    currentDate = addDays(currentDate, 1)
    daysIterated++
  }

  if (slots.length < numberOfSlots) {
    logError(new Error('The post slot manager could not generate all slots.'), {
      context: 'postSlotManager',
      metaData: { generatedSlots: slots.length, numberOfSlots },
    })
  }

  return slots
})

type GeneratePostingSlotsArgsBase = {
  postingSchedule: ScheduleV2[]
  timezone: string
}

type GeneratePostingSlotsArgs = GeneratePostingSlotsArgsBase &
  (
    | { endDate: string; numberOfSlots?: number; hasNextPage: true }
    | { endDate?: string; numberOfSlots: number; hasNextPage?: boolean }
  )

/**
 * Generate posting slots according to the provided arguments.
 * Slots will be generated until the later of endDate or the calculated date of the slot of numberOfSlots.
 */
const generatePostingSlots = memoize(function (
  args: GeneratePostingSlotsArgs,
): Slot[] {
  const { postingSchedule, timezone, endDate, numberOfSlots, hasNextPage } =
    args
  const enabledPostingSchedule = postingSchedule.filter((day) => !day.paused)

  const isPostingScheduleEmpty = enabledPostingSchedule.every(
    (slot) => !slot.times.length,
  )

  if (isPostingScheduleEmpty) {
    return []
  }

  if (hasNextPage) {
    return generatePostingSlotsUntilDate({
      postingSchedule: enabledPostingSchedule,
      timezone,
      endDate,
    })
  }

  const slotsByNumber = numberOfSlots
    ? generatePostingSlotsByNumber({
        postingSchedule: enabledPostingSchedule,
        timezone,
        numberOfSlots,
      })
    : []

  // Determine the effective end date for slot generation
  // This ensures we generate enough slots to include all scheduled posts,
  // preventing "ghost posts" from appearing out of place
  const lastSlotDate = slotsByNumber.at(-1)?.date
  let effectiveEndDate = endDate ? new Date(endDate) : undefined
  if (lastSlotDate) {
    if (!effectiveEndDate) {
      effectiveEndDate = new Date(lastSlotDate)
    } else if (endDate) {
      effectiveEndDate = max([endDate, lastSlotDate])
    }
  }

  if (!effectiveEndDate) {
    return []
  }

  const result = generatePostingSlotsUntilDate({
    postingSchedule: enabledPostingSchedule,
    timezone,
    endDate: effectiveEndDate.toISOString(),
  })
  return result
})

/**
 * Merge provided posts and slots into a single array.
 * The resulting array is sorted by date.
 *
 * @template T - The post fragment type (PostCard_PostData or CalendarPostCard_PostData)
 *
 * @param {Object} params - The parameters object
 * @param {T[]} params.posts - Array of posts to merge with slots
 * @param {Slot[]} params.slots - Array of time slots to merge with posts
 * @param {function} [params.callback] - Optional callback function called with the merged result
 * @param {boolean} [params.allowAnyPostToFillSlot] - Feature flag to control slot filling behavior (defaults to `false`):
 *   - When `false` (default): Only non-custom scheduled posts can fill slots with matching dates
 *   - When `true`: Any post (including custom scheduled) can fill slots with matching dates
 *
 * @returns {SlotOrPost<T>[]} - Merged and sorted array of posts and slots
 *
 * @description
 * This function merges posts and slots based on their dates and scheduling type:
 *
 * **Default behavior (allowAnyPostToFillSlot = false):**
 * - Non-custom scheduled posts with matching slot dates will "fill" the slot (slot is consumed)
 * - Custom scheduled posts will appear alongside their matching slots (both slot and post appear)
 *
 * **New behavior (allowAnyPostToFillSlot = true):**
 * - Any post with a matching slot date will "fill" the slot (slot is consumed)
 * - This includes both custom scheduled and non-custom scheduled posts
 *
 * **Sorting priority:**
 * 1. Posts are sorted by date first
 * 2. For posts with identical dates, non-custom scheduled posts come before custom scheduled posts
 * 3. The final result maintains chronological order
 *
 * @example
 * ```typescript
 * // Default behavior - only non-custom scheduled posts fill slots
 * const result1 = mergePostsAndSlots({
 *   posts: [customScheduledPost, regularPost],
 *   slots: [slot1, slot2],
 *   allowAnyPostToFillSlot: false
 * });
 *
 * // New behavior - any post can fill slots
 * const result2 = mergePostsAndSlots({
 *   posts: [customScheduledPost, regularPost],
 *   slots: [slot1, slot2],
 *   allowAnyPostToFillSlot: true
 * });
 * ```
 */
function mergePostsAndSlots<
  T extends PostCard_PostData | CalendarPostCard_PostData,
>({
  posts: _posts,
  slots,
  callback,
}: {
  posts: T[]
  slots: Slot[]
  callback?: (items: SlotOrPost<T>[]) => void
}): SlotOrPost<T>[] {
  const postsAndSlots: SlotOrPost<T>[] = []
  let i = 0
  let j = 0
  // Filter out posts that don't have a dueAt and sort them
  // First by date, then prioritize non-custom scheduled posts
  const posts = _posts
    .filter((post): post is T & { dueAt: string } => Boolean(post.dueAt))
    .toSorted((a, b) => {
      if (a.dueAt !== b.dueAt) {
        return a.dueAt < b.dueAt ? -1 : 1
      }
      // If dates are equal, non-custom scheduled posts come first
      if (!a.isCustomScheduled && b.isCustomScheduled) return -1
      if (a.isCustomScheduled && !b.isCustomScheduled) return 1
      return 0
    })

  slots.sort((a, b) => (a.date < b.date ? -1 : 1))

  // Merge the two sorted arrays
  while (i < posts.length && j < slots.length) {
    const postDate = posts[i].dueAt
    const slotDate = slots[j].date

    if (!postDate) {
      i++
      continue
    }
    if (!slotDate) {
      j++
      continue
    }

    // Any post with matching date fills the slot
    const postTakesUpSlot = postDate === slotDate

    if (postTakesUpSlot) {
      postsAndSlots.push(posts[i])
      i++
      // Skip the current slot because it's occupied by a post
      j++
    } else if (postDate < slotDate) {
      // This only works if date and dueAt are both datetime ISO strings
      postsAndSlots.push(posts[i])
      i++
    } else {
      postsAndSlots.push(slots[j])
      j++
    }
  }

  // Add any remaining elements from posts
  while (i < posts.length) {
    postsAndSlots.push(posts[i])
    i++
  }

  // Add any remaining elements from slots
  while (j < slots.length) {
    postsAndSlots.push(slots[j])
    j++
  }
  callback?.(postsAndSlots)
  return postsAndSlots
}

export { generatePostingSlots, mergePostsAndSlots }
